<script setup>
import { h, computed, onMounted } from 'vue';
import { provideSidebarContext } from './provider';
import { useAccount } from 'dashboard/composables/useAccount';
import { useKbd } from 'dashboard/composables/utils/useKbd';
import { useMapGetter } from 'dashboard/composables/store';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useStorage } from '@vueuse/core';
import { useSidebarKeyboardShortcuts } from './useSidebarKeyboardShortcuts';

import Button from 'dashboard/components-next/button/Button.vue';
import SidebarGroup from './SidebarGroup.vue';
import SidebarProfileMenu from './SidebarProfileMenu.vue';
import ChannelLeaf from './ChannelLeaf.vue';
import SidebarAccountSwitcher from './SidebarAccountSwitcher.vue';
import Logo from 'next/icon/Logo.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';
import SetupChecklistPopover from 'dashboard/components-next/onboarding/SetupChecklistPopover.vue';

defineProps({
  isMobileOpen: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'closeKeyShortcutModal',
  'openKeyShortcutModal',
  'showCreateAccountModal',
]);

const { accountScopedRoute } = useAccount();
const store = useStore();
const searchShortcut = useKbd([`$mod`, 'k']);
const { t } = useI18n();

const toggleShortcutModalFn = show => {
  if (show) {
    emit('openKeyShortcutModal');
  } else {
    emit('closeKeyShortcutModal');
  }
};

useSidebarKeyboardShortcuts(toggleShortcutModalFn);

// We're using localStorage to store the expanded item in the sidebar
// This helps preserve context when navigating between portal and dashboard layouts
// and also when the user refreshes the page
const expandedItem = useStorage(
  'next-sidebar-expanded-item',
  null,
  sessionStorage
);

const setExpandedItem = name => {
  expandedItem.value = expandedItem.value === name ? null : name;
};
provideSidebarContext({
  expandedItem,
  setExpandedItem,
});

const inboxes = useMapGetter('inboxes/getInboxes');
const teams = useMapGetter('teams/getMyTeams');
const conversationCustomViews = useMapGetter(
  'customViews/getConversationCustomViews'
);

onMounted(() => {
  store.dispatch('inboxes/get');
  store.dispatch('notifications/unReadCount');
  store.dispatch('teams/get');
  store.dispatch('attributes/get');
  store.dispatch('customViews/get', 'conversation');
});

const sortedInboxes = computed(() =>
  inboxes.value.slice().sort((a, b) => a.name.localeCompare(b.name))
);

const menuItems = computed(() => {
  const items = [
    {
      name: 'Home',
      icon: 'i-lucide-house',
      label: t('SIDEBAR.HOME'),
      to: accountScopedRoute('account_home'),
      activeOn: ['account_home'],
    },
    {
      name: 'AI',
      icon: 'i-woot-ai-chatbot',
      label: t('SIDEBAR.NERVENET'),
      children: [
        {
          name: 'Agent',
          icon: 'i-lucide-bot',
          label: t('SIDEBAR.NERVENET_AGENT'),
          to: accountScopedRoute('nervenet_home'),
        },
        {
          name: 'AI Supervisor',
          icon: 'i-lucide-user-check',
          label: t('SIDEBAR.NERVENET_SUPERVISOR'),
          to: accountScopedRoute('nervenet_supervisor'),
        },
        {
          name: 'Automation',
          label: t('SIDEBAR.AUTOMATION'),
          icon: 'i-lucide-workflow',
          to: accountScopedRoute('automation_list'),
          activeOn: ['automation_list'],
        },
        {
          name: 'Portals',
          label: t('SIDEBAR.HELP_CENTER.TITLE'),
          icon: 'i-lucide-library-big',
          activeOn: [
            'portals_articles_index',
            'portals_articles_new',
            'portals_articles_edit',
            'portals_faqs_index',
            'portals_categories_index',
            'portals_categories_articles_index',
            'portals_categories_articles_edit',
            'portals_locales_index',
            'portals_settings_index',
          ],
          to: accountScopedRoute('portals_index', {
            navigationPath: 'portals_articles_index',
          }),
        },
        /*
        {
          name: 'AI KB Search',
          icon: 'i-lucide-search',
          label: t('SIDEBAR.NERVENET_KB_SEARCH'),
          to: accountScopedRoute('nervenet_kb_search'),
        },
        */
        {
          name: 'Credits',
          icon: 'i-lucide-credit-card',
          label: t('SIDEBAR.NERVENET_PRICING'),
          to: accountScopedRoute('nervenet_pricing'),
        },
        /*
        {
          name: 'AI Knowledge',
          label: t('SIDEBAR.NERVENET_KNOWLEDGE'),
          to: accountScopedRoute('nervenet_knowledge'),
        },
        */
      ],
    },
    // Running a business, as opposed to running the AI that talks to its
    // customers — the business profile, bookings, and product/bill
    // management all live here regardless of which AI capabilities are
    // switched on. Not gated on a capability flag, same as these Nervenet
    // pages already behave server-side (Commerce::ProductsController and
    // Booking::BaseController#require_business both work without one).
    {
      name: 'Business',
      icon: 'i-lucide-briefcase',
      label: t('SIDEBAR.BUSINESS'),
      children: [
        {
          name: 'Business Info',
          icon: 'i-lucide-building-2',
          label: t('SIDEBAR.NERVENET_BOOKING_BUSINESS'),
          to: accountScopedRoute('nervenet_booking_business'),
        },
        {
          name: 'Bookings',
          icon: 'i-lucide-calendar',
          label: t('SIDEBAR.NERVENET_BOOKING'),
          to: accountScopedRoute('nervenet_booking_calendar'),
        },
        {
          name: 'Commerce',
          icon: 'i-lucide-shopping-cart',
          label: t('SIDEBAR.NERVENET_COMMERCE'),
          to: accountScopedRoute('nervenet_commerce'),
        },
        {
          name: 'Stay',
          icon: 'i-lucide-bed-double',
          label: t('SIDEBAR.NERVENET_STAY'),
          to: accountScopedRoute('nervenet_stay'),
        },
      ],
    },
    {
      name: 'Conversation',
      label: t('SIDEBAR.CONVERSATIONS'),
      icon: 'i-lucide-message-circle',
      children: [
        {
          name: 'All',
          label: t('SIDEBAR.ALL_CONVERSATIONS'),
          activeOn: ['inbox_conversation'],
          to: accountScopedRoute('home'),
        },
        {
          name: 'Inbox',
          label: t('SIDEBAR.INBOX'),
          to: accountScopedRoute('inbox_view'),
          activeOn: ['inbox_view', 'inbox_view_conversation'],
        },
        {
          name: 'Mentions',
          label: t('SIDEBAR.MENTIONED_CONVERSATIONS'),
          activeOn: ['conversation_through_mentions'],
          to: accountScopedRoute('conversation_mentions'),
        },
        {
          name: 'Folders',
          label: t('SIDEBAR.CUSTOM_VIEWS_FOLDER'),
          icon: 'i-lucide-folder',
          activeOn: ['conversations_through_folders'],
          children: conversationCustomViews.value.map(view => ({
            name: `${view.name}-${view.id}`,
            label: view.name,
            to: accountScopedRoute('folder_conversations', { id: view.id }),
          })),
        },
        {
          name: 'Teams',
          label: t('SIDEBAR.TEAMS'),
          icon: 'i-lucide-users',
          activeOn: ['conversations_through_team'],
          children: teams.value.map(team => ({
            name: `${team.name}-${team.id}`,
            label: team.name,
            to: accountScopedRoute('team_conversations', { teamId: team.id }),
          })),
        },
        {
          name: 'Channels',
          label: t('SIDEBAR.CHANNELS'),
          icon: 'i-lucide-inbox',
          activeOn: ['conversation_through_inbox'],
          children: sortedInboxes.value.map(inbox => ({
            name: `${inbox.name}-${inbox.id}`,
            label: inbox.name,
            to: accountScopedRoute('inbox_dashboard', { inbox_id: inbox.id }),
            component: leafProps =>
              h(ChannelLeaf, {
                label: leafProps.label,
                active: leafProps.active,
                inbox,
              }),
          })),
        },
        {
          name: 'Contacts',
          label: t('SIDEBAR.CONTACTS'),
          icon: 'i-lucide-contact',
          activeOn: [
            'contacts_dashboard_index',
            'contacts_edit',
            'contacts_dashboard_active',
            'contacts_dashboard_segments_index',
            'contacts_edit_segment',
            'contacts_dashboard_labels_index',
            'contacts_edit_label',
          ],
          to: accountScopedRoute(
            'contacts_dashboard_index',
            {},
            { page: 1, search: undefined }
          ),
        },
      ],
    },
    {
      name: 'Campaigns',
      label: t('SIDEBAR.CAMPAIGNS'),
      icon: 'i-lucide-megaphone',
      children: [
        {
          name: 'Live chat',
          label: t('SIDEBAR.LIVE_CHAT'),
          to: accountScopedRoute('campaigns_livechat_index'),
        },
        {
          name: 'SMS',
          label: t('SIDEBAR.SMS'),
          to: accountScopedRoute('campaigns_sms_index'),
        },
        {
          name: 'WhatsApp',
          label: t('SIDEBAR.WHATSAPP'),
          to: accountScopedRoute('campaigns_whatsapp_index'),
        },
      ],
    },
    {
      name: 'Settings',
      label: t('SIDEBAR.SETTINGS'),
      icon: 'i-lucide-bolt',
      children: [
        {
          name: 'Settings Account Settings',
          label: t('SIDEBAR.ACCOUNT_SETTINGS'),
          icon: 'i-lucide-briefcase',
          to: accountScopedRoute('general_settings_index'),
        },
        {
          name: 'Settings Agents',
          label: t('SIDEBAR.AGENTS'),
          icon: 'i-lucide-square-user',
          to: accountScopedRoute('agent_list'),
        },
        {
          name: 'Settings Teams',
          label: t('SIDEBAR.TEAMS'),
          icon: 'i-lucide-users',
          to: accountScopedRoute('settings_teams_list'),
        },
        {
          name: 'Settings Inboxes',
          label: t('SIDEBAR.INBOXES'),
          icon: 'i-lucide-inbox',
          to: accountScopedRoute('settings_inbox_list'),
        },
        {
          name: 'Settings Labels',
          label: t('SIDEBAR.LABELS'),
          icon: 'i-lucide-tags',
          to: accountScopedRoute('labels_list'),
        },
        {
          name: 'Settings Custom Attributes',
          label: t('SIDEBAR.CUSTOM_ATTRIBUTES'),
          icon: 'i-lucide-code',
          to: accountScopedRoute('attributes_list'),
        },
        {
          name: 'Settings Automation',
          label: t('SIDEBAR.AUTOMATION'),
          icon: 'i-lucide-workflow',
          to: accountScopedRoute('automation_list'),
        },
        {
          name: 'Settings Agent Bots',
          label: t('SIDEBAR.AGENT_BOTS'),
          icon: 'i-lucide-bot',
          to: accountScopedRoute('agent_bots'),
        },
        {
          name: 'Settings Macros',
          label: t('SIDEBAR.MACROS'),
          icon: 'i-lucide-toy-brick',
          to: accountScopedRoute('macros_wrapper'),
        },
        {
          name: 'Settings Canned Responses',
          label: t('SIDEBAR.CANNED_RESPONSES'),
          icon: 'i-lucide-message-square-quote',
          to: accountScopedRoute('canned_list'),
        },
        {
          name: 'Settings Integrations',
          label: t('SIDEBAR.INTEGRATIONS'),
          icon: 'i-lucide-blocks',
          to: accountScopedRoute('settings_applications'),
        },
        {
          name: 'Settings Audit Logs',
          label: t('SIDEBAR.AUDIT_LOGS'),
          icon: 'i-lucide-briefcase',
          to: accountScopedRoute('auditlogs_list'),
        },
        {
          name: 'Settings Custom Roles',
          label: t('SIDEBAR.CUSTOM_ROLES'),
          icon: 'i-lucide-shield-plus',
          to: accountScopedRoute('custom_roles_list'),
        },
        {
          name: 'Settings Sla',
          label: t('SIDEBAR.SLA'),
          icon: 'i-lucide-clock-alert',
          to: accountScopedRoute('sla_list'),
        },
        {
          name: 'Settings Billing',
          label: t('SIDEBAR.BILLING'),
          icon: 'i-lucide-credit-card',
          to: accountScopedRoute('billing_settings_index'),
        },
      ],
    },
  ];

  const primaryOrder = [
    'Home',
    'Conversation',
    'AI',
    'Business',
    'Campaigns',
    'Settings',
  ];
  return primaryOrder.map(name => items.find(item => item.name === name));
});
</script>

<template>
  <aside
    class="w-[200px] bg-n-slate-3 dark:bg-n-solid-2 rtl:border-l ltr:border-r border-n-weak h-screen flex flex-col text-sm pb-1 fixed top-0 ltr:left-0 rtl:right-0 z-50 transition-[transform,box-shadow] duration-300 ease-[cubic-bezier(0.23,1,0.32,1)] md:relative md:top-auto md:left-auto md:right-auto md:z-auto md:translate-x-0 md:shadow-none"
    :class="
      isMobileOpen
        ? 'translate-x-0 shadow-[0_24px_80px_-8px_rgba(0,0,0,0.28),0_8px_24px_-4px_rgba(0,0,0,0.16)]'
        : '-translate-x-full md:translate-x-0'
    "
  >
    <section class="grid gap-2 mt-3 mb-3">
      <div class="flex items-center min-w-0 gap-2 px-3">
        <div class="grid flex-shrink-0 size-6 place-content-center">
          <Logo class="size-4" />
        </div>
        <div class="flex-shrink-0 w-px h-3 bg-n-strong" />
        <SidebarAccountSwitcher
          class="flex-grow min-w-0 -mx-1"
          @show-create-account-modal="emit('showCreateAccountModal')"
        />
      </div>
      <div class="flex gap-1.5 px-2">
        <RouterLink
          :to="{ name: 'search' }"
          class="flex items-center w-full gap-2 px-2.5 py-1 rounded-lg h-8 outline outline-1 outline-n-weak bg-n-solid-3 dark:bg-n-black/30 hover:outline-n-slate-6 transition-[outline-color] duration-150"
        >
          <span
            class="flex-shrink-0 i-lucide-search size-3.5 text-n-slate-10"
          />
          <span class="flex-grow text-left text-n-slate-10 text-xs">
            {{ t('COMBOBOX.SEARCH_PLACEHOLDER') }}
          </span>
          <span
            class="hidden tracking-wide pointer-events-none select-none text-n-slate-9 text-xs"
          >
            {{ searchShortcut }}
          </span>
        </RouterLink>
        <ComposeConversation align-position="right">
          <template #trigger="{ toggle }">
            <Button
              icon="i-lucide-pen-line"
              color="slate"
              size="sm"
              class="!h-8 !bg-n-solid-3 dark:!bg-n-black/30 !outline-n-weak !text-n-slate-11 hover:!outline-n-slate-6"
              @click="toggle"
            />
          </template>
        </ComposeConversation>
      </div>
      <SetupChecklistPopover />
    </section>
    <nav class="grid flex-grow gap-2 px-2 pb-4 overflow-y-scroll no-scrollbar">
      <ul class="flex flex-col gap-0.5 m-0 list-none">
        <SidebarGroup
          v-for="item in menuItems"
          :key="item.name"
          v-bind="item"
        />
      </ul>
    </nav>
    <section class="px-1 py-1.5 border-t border-n-weak flex-shrink-0">
      <SidebarProfileMenu
        @open-key-shortcut-modal="emit('openKeyShortcutModal')"
      />
    </section>
  </aside>
</template>
